import { useEffect, useRef } from 'react';

const pushBlockState = (): void => {
  window.history.pushState({ __block: true }, '', window.location.href);
};

let ignoreNextPopstate = false;

/**
 * Reset module state for tests. Avoids "allows back" test leaving ignoreNextPopstate true.
 * @internal
 */
export const __testingResetBackBlockerState = (): void => {
  ignoreNextPopstate = false;
};

/** Blocks browser back; onConfirm(allowBack) to optionally proceed. Pops block on unmount only if still on same URL. */
export const useBackBlocker = (onConfirm: (allowBack: () => void) => void): void => {
  const allowingBackRef = useRef(false);
  const onConfirmRef = useRef(onConfirm);

  useEffect(() => {
    onConfirmRef.current = onConfirm;
  }, [onConfirm]);

  useEffect(() => {
    const currentPageUrl = window.location.href;
    pushBlockState();
    const onPopState = (): void => {
      if (ignoreNextPopstate) {
        ignoreNextPopstate = false;
        return;
      }
      if (allowingBackRef.current) {
        allowingBackRef.current = false;
        ignoreNextPopstate = true;
        window.history.back();
        return;
      }
      // Undo the back by going forward (pushState same URL may replace, not push, in this env)
      ignoreNextPopstate = true;
      window.history.go(1);
      onConfirmRef.current(() => {
        allowingBackRef.current = true;
        window.history.back();
      });
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (window.history.state?.__block === true && window.location.href === currentPageUrl) {
        window.history.back();
      }
    };
  }, []);
};
